import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ShieldCheck, Loader2, PencilLine, RefreshCw, AlertCircle } from 'lucide-react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { roleHome } from '../utils/roleHome';

/**
 * iter429 — Universal SMS phone verification gate.
 *
 * Every talent / client / agency must prove their mobile number is reachable
 * via a one-time SMS code before the platform unlocks. This full-screen page
 * is reached whenever `user.phone_verified === false`.
 */
const VerifyPhone = () => {
  const navigate = useNavigate();
  const { user, loading, logout, refreshUser } = useAuth();

  const [step, setStep] = useState('loading'); // loading | enter_number | enter_code
  const [phone, setPhone] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [editingNumber, setEditingNumber] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/phone-verification/status');
      if (data.phone_verified) {
        await refreshUser();
        navigate(roleHome(user?.role), { replace: true });
        return;
      }
      setPhone(data.phone || '');
      setMaskedPhone(data.masked_phone || '');
      if (data.pending) {
        setRequestId(data.pending.request_id);
        setMaskedPhone(data.pending.masked_phone || data.masked_phone || '');
        setStep('enter_code');
      } else {
        // If no number on file or it's malformed, force them to enter one.
        setEditingNumber(!data.phone || !data.valid_format);
        if (!data.valid_format && data.format_issue) setError(data.format_issue);
        setStep('enter_number');
      }
    } catch (e) {
      setStep('enter_number');
    }
  }, [navigate, refreshUser, user]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Guard: only an authenticated user can be on this gate.
  useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendCode = async () => {
    setError(''); setInfo(''); setBusy(true);
    try {
      const payload = editingNumber && phone ? { phone } : {};
      const { data } = await api.post('/phone-verification/send', payload);
      setMaskedPhone(data.masked_phone || maskedPhone);
      const devc = data.dev_code || null;
      setDevCode(devc);
      const okStatuses = ['sent', 'queued', 'accepted', 'delivered', 'sandbox'];
      const delivered = okStatuses.includes((data.delivery_status || '').toLowerCase());
      if (!delivered && !devc) {
        // A real technical delivery failure — don't strand the user on the
        // code screen waiting for a text that will never arrive.
        setError("We couldn't text that number right now. Please double-check it or try again in a moment.");
        return;
      }
      setRequestId(data.request_id);
      setCode('');
      setCooldown(30);
      setStep('enter_code');
      setInfo(data.message || 'Code sent.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send the code. Please check your number.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setError(''); setInfo(''); setBusy(true);
    try {
      await api.post('/phone-verification/verify', { request_id: requestId, code: code.trim() });
      await refreshUser();
      navigate(roleHome(user?.role), { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || 'That code was incorrect. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (cooldown > 0) return;
    setError(''); setInfo(''); setBusy(true);
    try {
      const { data } = await api.post('/phone-verification/resend', { request_id: requestId });
      setDevCode(data.dev_code || null);
      setCooldown(30);
      setInfo('A new code was sent.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not resend the code.');
    } finally {
      setBusy(false);
    }
  };

  const changeNumber = () => {
    setEditingNumber(true);
    setStep('enter_number');
    setCode('');
    setRequestId(null);
    setError(''); setInfo('');
  };

  return (
    <div
      data-testid="verify-phone-page"
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: 'radial-gradient(1200px 600px at 50% -10%, #0e7490 0%, #083344 45%, #041f28 100%)' }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="px-8 pt-8 pb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-50">
            {step === 'enter_code'
              ? <ShieldCheck className="h-8 w-8 text-cyan-700" />
              : <Phone className="h-8 w-8 text-cyan-700" />}
          </div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="verify-phone-title">
            {step === 'enter_code' ? 'Enter your verification code' : 'Verify your phone number'}
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {step === 'enter_code'
              ? <>We sent a 6-digit code to <span className="font-semibold text-slate-700">{maskedPhone}</span>.</>
              : 'We need to confirm a working mobile number so we can reach you about your gigs, confirmations and urgent updates.'}
          </p>
        </div>

        <div className="px-8 pb-8 space-y-4">
          {step === 'loading' && (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-cyan-700" /></div>
          )}

          {step === 'enter_number' && (
            <>
              {!editingNumber && maskedPhone ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400">Your number</div>
                    <div className="text-lg font-semibold text-slate-800" data-testid="verify-phone-current">{maskedPhone}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditingNumber(true)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-cyan-700 hover:text-cyan-900"
                    data-testid="verify-phone-edit-btn"
                  >
                    <PencilLine className="h-4 w-4" /> Change
                  </button>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Mobile phone number</label>
                  <input
                    data-testid="verify-phone-input"
                    type="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-lg outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  />
                  <p className="mt-1 text-xs text-slate-400">US numbers need 10 digits. Include your country code for international numbers.</p>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 text-sm text-red-600" data-testid="verify-phone-error">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}

              <button
                data-testid="verify-phone-send-btn"
                onClick={sendCode}
                disabled={busy || (editingNumber && !phone.trim())}
                className="w-full rounded-xl bg-cyan-600 py-3 text-base font-semibold text-white transition-colors hover:bg-cyan-700 disabled:opacity-50"
              >
                {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Send verification code'}
              </button>
            </>
          )}

          {step === 'enter_code' && (
            <>
              <input
                data-testid="verify-phone-code-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="______"
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-center text-2xl font-mono tracking-[0.5em] outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
              />

              {devCode && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="verify-phone-devcode">
                  Preview mode (SMS blocked): your code is <span className="font-bold">{devCode}</span>
                </div>
              )}

              {info && <div className="text-sm text-emerald-600" data-testid="verify-phone-info">{info}</div>}
              {error && (
                <div className="flex items-start gap-2 text-sm text-red-600" data-testid="verify-phone-error">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}

              <button
                data-testid="verify-phone-verify-btn"
                onClick={verifyCode}
                disabled={busy || code.length < 6}
                className="w-full rounded-xl bg-cyan-600 py-3 text-base font-semibold text-white transition-colors hover:bg-cyan-700 disabled:opacity-50"
              >
                {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'Verify & continue'}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={cooldown > 0 || busy}
                  className="inline-flex items-center gap-1 font-medium text-cyan-700 hover:text-cyan-900 disabled:text-slate-400"
                  data-testid="verify-phone-resend-btn"
                >
                  <RefreshCw className="h-4 w-4" /> {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                </button>
                <button
                  type="button"
                  onClick={changeNumber}
                  className="font-medium text-slate-500 hover:text-slate-700"
                  data-testid="verify-phone-change-number-btn"
                >
                  Change number
                </button>
              </div>
            </>
          )}

          <button
            type="button"
            onClick={() => { logout(); navigate('/login', { replace: true }); }}
            className="w-full pt-2 text-center text-xs text-slate-400 hover:text-slate-600"
            data-testid="verify-phone-logout-btn"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyPhone;
